import { AdminApiError } from "../api/api-error";

interface Options {
  apiOrigin: string;
  getToken: () => Promise<string>;
  navigate: (url: string) => void;
}

/** Code 15 requires an ID token at its origin; code 16 is subscription navigation and does not receive credentials. */
export function createAuthorizationHandler({ apiOrigin, getToken, navigate }: Options) {
  let navigating = false;

  return async (input: unknown) => {
    if (
      typeof input !== "object" ||
      input === null ||
      !("code" in input) ||
      (input.code !== 15 && input.code !== 16)
    ) {
      return;
    }

    const code = input.code;
    const msg = "msg" in input && typeof input.msg === "string" ? input.msg : "";

    let url: URL;
    try {
      url = new URL(msg);
    } catch {
      throw new AdminApiError(
        "BlyCare returned an untrusted authorization destination.",
        code,
        "authorization",
      );
    }

    if (url.protocol !== "https:" || url.username || url.password) {
      throw new AdminApiError(
        "BlyCare returned an untrusted authorization destination.",
        code,
        "authorization",
      );
    }

    const trustedOrigin = new URL(apiOrigin, window.location.origin).origin;
    const trustedApi = url.origin === trustedOrigin;
    const shopify =
      url.hostname === "admin.shopify.com" ||
      /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(url.hostname);

    if (code === 15 ? !trustedApi : !(trustedApi || shopify)) {
      throw new AdminApiError(
        "BlyCare returned an untrusted authorization destination.",
        code,
        "authorization",
      );
    }

    if (navigating) {
      return;
    }
    navigating = true;

    if (code === 15) {
      try {
        url.searchParams.set("id_token", await getToken());
      } catch {
        // Session tokens are often unavailable on the first-install top-level bounce.
      }
    }

    navigate(url.href);
  };
}

/** Legacy AppLayout scope-update flow: backend URL is already complete, no ID token added. */
export function createScopeUpdateHandler(navigate: (url: string) => void) {
  let navigating = false;

  return (destination: string, shop: string) => {
    let url: URL;
    try {
      url = new URL(destination);
    } catch {
      throw new AdminApiError(
        "BlyCare returned an untrusted permission update destination.",
        null,
        "authorization",
      );
    }

    if (url.protocol !== "https:" || url.username || url.password) {
      throw new AdminApiError(
        "BlyCare returned an untrusted permission update destination.",
        null,
        "authorization",
      );
    }

    if (url.hostname !== shop && url.hostname !== "admin.shopify.com") {
      throw new AdminApiError(
        "BlyCare returned an untrusted permission update destination.",
        null,
        "authorization",
      );
    }

    if (navigating) {
      return;
    }
    navigating = true;
    navigate(url.href);
  };
}
